/**
 * AddCustomTagsModal Component
 *
 * Modal for adding custom tags to a deployment.
 * Note: Tags are append-only, cannot be removed once added. Max 10 tags per deployment.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { customTagSchema } from '../../../lib/validators/deployment';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { useAddCustomTags } from '../hooks/useAddCustomTags';
import { getErrorMessage } from '../../../lib/utils/errors';
import { XMarkIcon } from '@heroicons/react/20/solid';

interface AddCustomTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  deployment: DeploymentSanitized;
  onSuccess?: () => void;
}

const MAX_TAGS = 10;

/**
 * AddCustomTagsModal component
 */
export const AddCustomTagsModal: React.FC<AddCustomTagsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  deployment,
  onSuccess,
}) => {
  const [newTagInput, setNewTagInput] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  const existingTags = deployment.customTags || [];
  const totalTagsAfterAdd = existingTags.length + tagsToAdd.length;
  const remainingSlots = MAX_TAGS - existingTags.length;

  const addCustomTagsMutation = useAddCustomTags({
    tenantId,
    applicationId,
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    if (!addCustomTagsMutation.isPending) {
      setNewTagInput('');
      setTagsToAdd([]);
      setValidationError(null);
      addCustomTagsMutation.reset();
      onClose();
    }
  };

  const handleAddTag = () => {
    const trimmedTag = newTagInput.trim();

    if (!trimmedTag) {
      return;
    }

    // Validate the tag
    const result = customTagSchema.safeParse(trimmedTag);
    if (!result.success) {
      setValidationError(result.error.errors[0].message);
      return;
    }

    // Check for duplicates
    if (existingTags.includes(trimmedTag) || tagsToAdd.includes(trimmedTag)) {
      setValidationError('This tag already exists');
      return;
    }

    // Check max limit
    if (totalTagsAfterAdd >= MAX_TAGS) {
      setValidationError(`Maximum ${MAX_TAGS} tags allowed`);
      return;
    }

    setTagsToAdd([...tagsToAdd, trimmedTag]);
    setNewTagInput('');
    setValidationError(null);
  };

  const handleRemoveTagToAdd = (tag: string) => {
    setTagsToAdd(tagsToAdd.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = () => {
    if (tagsToAdd.length === 0) {
      setValidationError('Add at least one tag');
      return;
    }
    addCustomTagsMutation.mutate({
      deploymentId: deployment.deploymentId,
      customTags: tagsToAdd,
    });
  };

  const isLoading = addCustomTagsMutation.isPending;
  const error = addCustomTagsMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Add Custom Tags</Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Info about tags */}
          <Alert variant="info">
            <div className="space-y-1">
              <p className="text-sm">
                Tags help you organize and identify deployments. Once added, tags cannot be removed.
              </p>
              <p className="text-xs">
                Examples: stable, production-ready, reviewed, hotfix
              </p>
            </div>
          </Alert>

          {/* Deployment Info */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Deployment:</span>
              <span className="text-gray-900 font-mono text-sm">
                {deployment.deploymentId.slice(0, 8)}...
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Tags Used:</span>
              <span className="text-gray-900">
                {existingTags.length}/{MAX_TAGS}
              </span>
            </div>
          </div>

          {/* Existing Tags (read-only) */}
          {existingTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Existing Tags (cannot be removed)
              </label>
              <div className="flex flex-wrap gap-2">
                {existingTags.map((tag) => (
                  <Badge key={tag} variant="neutral" size="md">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* New Tags to Add */}
          {tagsToAdd.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags to Add
              </label>
              <div className="flex flex-wrap gap-2">
                {tagsToAdd.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTagToAdd(tag)}
                      disabled={isLoading}
                      className="hover:text-green-600 focus:outline-none"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add Tag Input */}
          {remainingSlots > tagsToAdd.length ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Add New Tag ({remainingSlots - tagsToAdd.length} slots remaining)
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., stable"
                  value={newTagInput}
                  onChange={(e) => {
                    setNewTagInput(e.target.value);
                    setValidationError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  error={validationError || undefined}
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddTag}
                  disabled={isLoading || !newTagInput.trim()}
                >
                  Add
                </Button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Press Enter or click Add to add a tag
              </p>
            </div>
          ) : (
            <Alert variant="warning">
              Maximum number of tags reached ({MAX_TAGS}).
            </Alert>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading || tagsToAdd.length === 0}
          >
            Add {tagsToAdd.length} Tag{tagsToAdd.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
